import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  ExternalLink,
  MapPin,
  MessageSquare,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Trophy,
  UserPlus,
  Users,
} from "lucide-react";
import { formatDateTime } from "../../lib/format";
import { currentIntlLocale } from "../../lib/i18n";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Badge } from "@v2/components/ui/badge";
import { Input } from "@v2/components/ui/input";
import { Switch } from "@v2/components/ui/switch";
import { ShareTextDialog } from "@v2/components/ShareTextDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { useUsersDisplay, userShortName } from "../../lib/users-display";
import { fetchAllPages } from "../../lib/workspace-data";
import { isEvaluatingGuest, isSquadPlayer } from "../../lib/roster";
import { seasonErrorMessage, useSeasonScope } from "../../lib/seasons";
import {
  shiftIsoDays,
  useCallupPresentMutation,
  useMagicLinkMutation,
  useNotifyCallupsMutation,
  useSessionCallupUpsert,
  useSessionCallups,
  useSessionMessageMutation,
  useCallupListMessageMutation,
  useSessionUpdateMutation,
} from "../../lib/sessions";
import { useEvents, useTeamCompetitions, useTeamOpponents } from "../../lib/team-config";
import { TeamSuggestions } from "../../components/team-suggestions";
import { EmptyState, SectionLoading } from "../../components/state";
import type {
  Match,
  MatchPlayerStat,
  Player,
  ResponseStatus,
  Session,
  SessionResponse,
} from "../../types";
import { SessionAttendanceBoard, SessionToolbar, getResponseCounts } from "./EventsView";
import { MatchOpenPanel } from "../match/MatchOpenPanel";
import { CompetitionPicker } from "../../components/competition-picker";
import { AttendanceBar, CountMark, SessionDate, StatTileLabel, TimeSections } from "./parts";

// Selettore competizione dentro il form a FormData dell'aggiunta rapida:
// tiene il valore scelto (comodo per piu' partite della stessa competizione)
// e lo espone come input nascosto competition_name.
function QuickCompetitionField({ teamId }: { teamId: string }) {
  const [value, setValue] = useState("");
  return <CompetitionPicker teamId={teamId} name="competition_name" value={value} onChange={setValue} className="col-span-2 sm:w-[190px]" />;
}
import { track } from "../../lib/analytics";

export function MatchDaysView({ teamId }: { teamId: string }) {
  const { t } = useTranslation("sessions");
  const queryClient = useQueryClient();
  const intlLocale = currentIntlLocale();
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [openMatchId, setOpenMatchId] = useState<string | null>(null);
  const [scrollToMatchId, setScrollToMatchId] = useState<string | null>(null);

  useEffect(() => {
    if (!scrollToMatchId) return;
    const target = document.getElementById(`match-${scrollToMatchId}`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollToMatchId(null);
    }
  }, [scrollToMatchId, openEventId, openMatchId]);
  const [matchSearchTerm, setMatchSearchTerm] = useState("");
  const [responseSearchTerm, setResponseSearchTerm] = useState("");
  const [showOnlyMismatches, setShowOnlyMismatches] = useState(false);
  const eventsQuery = useEvents(teamId, "match_day");
  const seasonScope = useSeasonScope();
  const playersQuery = useQuery({
    queryKey: ["players", teamId, "matchdays"],
    queryFn: async () => {
      const { data, error } = await supabaseV2.from("players").select("*").eq("team_id", teamId).order("last_name");
      if (error) throw error;
      return (data || []) as Player[];
    },
  });
  const matchesQuery = useQuery({
    queryKey: ["matches", teamId, "matchdays", seasonScope.key],
    enabled: seasonScope.ready,
    queryFn: async () => {
      let query = supabaseV2.from("matches").select("*, sessions!inner(season_id)").eq("team_id", teamId);
      if (seasonScope.seasonId) query = query.eq("sessions.season_id", seasonScope.seasonId);
      const { data, error } = await query.order("starts_at", { ascending: false });
      if (error) throw error;
      return (data || []).map(({ sessions, ...row }: any) => ({ ...row, season_id: sessions?.season_id || null })) as Match[];
    },
  });
  const opponentsQuery = useTeamOpponents(teamId);
  const competitionsQuery = useTeamCompetitions(teamId);
  const statsQuery = useQuery({
    queryKey: ["match-player-stats", teamId, "matchdays", seasonScope.key],
    enabled: seasonScope.ready,
    queryFn: async () => {
      return fetchAllPages((from, to) => {
        let query = supabaseV2
          .from("match_player_stats")
          .select("id, match_id, player_id, goals, assists, yellow_cards, red_cards, started, matches!inner(team_id, sessions!inner(season_id))")
          .eq("matches.team_id", teamId);
        if (seasonScope.seasonId) query = query.eq("matches.sessions.season_id", seasonScope.seasonId);
        return query.range(from, to);
      }) as Promise<MatchPlayerStat[]>;
    },
  });
  const responsesQuery = useQuery({
    queryKey: ["responses", teamId, "matchdays", seasonScope.key],
    enabled: seasonScope.ready,
    queryFn: async () => {
      return fetchAllPages((from, to) => {
        let query = supabaseV2
          .from("session_responses")
          .select("id, session_id, player_id, status, source, sessions!inner(team_id, season_id)")
          .eq("sessions.team_id", teamId);
        if (seasonScope.seasonId) query = query.eq("sessions.season_id", seasonScope.seasonId);
        return query.range(from, to);
      }) as Promise<SessionResponse[]>;
    },
  });
  const callupsQuery = useSessionCallups(teamId);
  const updateSessionResponse = useMutation({
    mutationFn: async ({ eventId, playerId, status }: { eventId: string; playerId: string; status: ResponseStatus }) => {
      const { error } = await supabaseV2.from("session_responses").upsert(
        {
          session_id: eventId,
          player_id: playerId,
          status,
          source: "staff_override",
          responded_at: status === "no_response" ? null : new Date().toISOString(),
        },
        { onConflict: "session_id,player_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["responses", teamId, "matchdays"] });
      queryClient.invalidateQueries({ queryKey: ["responses", teamId] });
    },
  });
  const createMagicLink = useMagicLinkMutation(teamId);
  const updateCallup = useSessionCallupUpsert(teamId);
  const callupPresent = useCallupPresentMutation(teamId);
  const updateMatchDayEvent = useSessionUpdateMutation(teamId);
  const copySessionMessage = useSessionMessageMutation();
  const [copiedMessageEventId, setCopiedMessageEventId] = useState<string | null>(null);
  const copyCallupList = useCallupListMessageMutation();
  const [copiedCallupsEventId, setCopiedCallupsEventId] = useState<string | null>(null);
  const notifyCallups = useNotifyCallupsMutation(teamId);
  const onNotifyCallupsForEvent = (sessionId: string, pendingCount: number) => {
    if (pendingCount === 0) return;
    if (!window.confirm(t("board.notifyCallupsConfirm", { count: pendingCount }))) return;
    notifyCallups.mutate(sessionId, {
      onSuccess: ({ sent, already }) => {
        track.callupSent({ team_id: teamId, session_id: sessionId, sent, already });
        const msg =
          already > 0
            ? t("board.notifyCallupsResultOkWithAlready", { sent, already })
            : t("board.notifyCallupsResultOk", { sent });
        window.alert(msg);
      },
      onError: (e: any) => {
        window.alert(`${t("board.notifyCallupsError")}: ${e?.message || e}`);
      },
    });
  };
  const [shareDialog, setShareDialog] = useState<{ open: boolean; title: string; text: string }>({
    open: false,
    title: "",
    text: "",
  });

  const duplicateMatchDay = useMutation({
    mutationFn: async (event: Session) => {
      const { data: newEvent, error: eventError } = await supabaseV2
        .from("sessions")
        .insert({
          team_id: teamId,
            title: `${event.title} copia`,
          starts_at: shiftIsoDays(event.starts_at, 7),
          ends_at: shiftIsoDays(event.ends_at, 7),
          location: event.location,
          communication_details: event.communication_details,
          notes: event.notes,
          response_deadline_at: shiftIsoDays(event.response_deadline_at || event.starts_at, 7),
          status: "scheduled",
          // Match day duplicata: marchiamo gia' has_matches=true cosi' la
          // notifica push parte come "Nuova partita". Trigger lato DB
          // ricalcolera' comunque dopo l'INSERT dei matches.
          has_matches: true,
        })
        .select("id")
        .single();
      if (eventError) throw eventError;

      const matchesToCopy = (matchesQuery.data || []).filter((m) => m.session_id === event.id);
      if (matchesToCopy.length > 0) {
        const { error: matchesError } = await supabaseV2.from("matches").insert(
          matchesToCopy.map((match) => ({
            session_id: newEvent.id,
            team_id: teamId,
            opponent_id: null,
            competition_id: match.competition_id ?? null,
            starts_at: shiftIsoDays(match.starts_at || event.starts_at, 7),
            opponent_name: match.opponent_name,
            home_away: match.home_away,
            competition_name: match.competition_name,
            our_score: null,
            opponent_score: null,
            result_notes: null,
            sort_order: match.sort_order || 0,
          }))
        );
        if (matchesError) throw matchesError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "match_day"] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
    },
    onError: (e: any) => window.alert(seasonErrorMessage(e) || e?.message || String(e)),
  });
  const deleteMatchDay = useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabaseV2.from("sessions").delete().eq("id", eventId).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      setOpenEventId(null);
      setOpenMatchId(null);
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "match_day"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "all"] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId, "matchdays"] });
    },
  });
  const duplicateMatch = useMutation({
    mutationFn: async (match: Match) => {
      const { error } = await supabaseV2.from("matches").insert({
        session_id: match.session_id,
        team_id: teamId,
        opponent_id: null,
        competition_id: match.competition_id ?? null,
        starts_at: match.starts_at,
        opponent_name: `${match.opponent_name} copia`,
        home_away: match.home_away,
        competition_name: match.competition_name,
        our_score: null,
        opponent_score: null,
        result_notes: null,
        sort_order: (match.sort_order || 0) + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
    },
  });
  const createMatch = useMutation({
    mutationFn: async ({ event, formData }: { event: Session; formData: FormData }) => {
      const date = String(formData.get("date") || event.starts_at.slice(0, 10));
      const time = String(formData.get("time") || "12:00");
      const { error } = await supabaseV2.from("matches").insert({
        session_id: event.id,
        team_id: teamId,
        // BUGFIX: stringa senza tz veniva trattata UTC dal DB e shifted
        // di +offset al re-render. Convertiamo locale → UTC esplicitamente.
        starts_at: new Date(`${date}T${time}`).toISOString(),
        opponent_name: String(formData.get("opponent_name")),
        home_away: String(formData.get("home_away") || "neutral"),
        competition_name: String(formData.get("competition_name") || "") || null,
        our_score: formData.get("our_score") ? Number(formData.get("our_score")) : null,
        opponent_score: formData.get("opponent_score") ? Number(formData.get("opponent_score")) : null,
        sort_order: formData.get("sort_order") ? Number(formData.get("sort_order")) : 0,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
    },
  });
  const updateMatch = useMutation({
    mutationFn: async ({ matchId, payload }: { matchId: string; payload: Partial<Match> }) => {
      const { error } = await supabaseV2.from("matches").update(payload).eq("id", matchId).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
      queryClient.invalidateQueries({ queryKey: ["seasons", teamId] });
    },
    onError: (e: any) => window.alert(seasonErrorMessage(e) || e?.message || String(e)),
  });
  const deleteMatch = useMutation({
    mutationFn: async (matchId: string) => {
      const { error } = await supabaseV2.from("matches").delete().eq("id", matchId).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      setOpenMatchId(null);
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId, "matchdays"] });
    },
  });

  const updateMatchScore = useMutation({
    mutationFn: async ({ matchId, ourScore, opponentScore }: { matchId: string; ourScore: number; opponentScore: number }) => {
      const { error } = await supabaseV2
        .from("matches")
        .update({ our_score: ourScore, opponent_score: opponentScore })
        .eq("id", matchId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
    },
  });

  const updatePlayerStat = useMutation({
    mutationFn: async (stat: MatchPlayerStat) => {
      const { error } = await supabaseV2
        .from("match_player_stats")
        .update({
          goals: stat.goals,
          assists: stat.assists,
          yellow_cards: stat.yellow_cards,
          red_cards: stat.red_cards,
          started: stat.started,
        })
        .eq("id", stat.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId, "matchdays"] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
    },
  });
  const upsertPlayerStat = useMutation({
    mutationFn: async (stat: Omit<MatchPlayerStat, "id">) => {
      const { error } = await supabaseV2.from("match_player_stats").upsert(
        {
          match_id: stat.match_id,
          player_id: stat.player_id,
          goals: stat.goals,
          assists: stat.assists,
          yellow_cards: stat.yellow_cards,
          red_cards: stat.red_cards,
          started: stat.started,
        },
        { onConflict: "match_id,player_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId, "matchdays"] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
    },
  });
  const markStarters = useMutation({
    mutationFn: async ({ matchId, playerIds }: { matchId: string; playerIds: string[] }) => {
      if (playerIds.length === 0) return;
      const { error } = await supabaseV2.from("match_player_stats").upsert(
        playerIds.map((playerId) => ({ match_id: matchId, player_id: playerId, started: true })),
        { onConflict: "match_id,player_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId, "matchdays"] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
    },
  });
  const clearStarters = useMutation({
    mutationFn: async ({ matchId }: { matchId: string }) => {
      const { error } = await supabaseV2
        .from("match_player_stats")
        .update({ started: false })
        .eq("match_id", matchId)
        .eq("started", true);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId, "matchdays"] });
      queryClient.invalidateQueries({ queryKey: ["match-player-stats", teamId] });
    },
  });

  // Reference updatePlayerStat to silence unused warnings in case App.tsx still owns
  // call sites; the hook is preserved here to mirror the original local mutations.
  void updatePlayerStat;

  const statsByMatch = useMemo(() => {
    const map = new Map<string, MatchPlayerStat[]>();
    for (const stat of statsQuery.data || []) {
      const current = map.get(stat.match_id) || [];
      current.push(stat);
      map.set(stat.match_id, current);
    }
    return map;
  }, [statsQuery.data]);

  const goalMismatches = useMemo(() => {
    return (matchesQuery.data || [])
      .map((match) => {
        // Gli autogol avversari coprono gol senza marcatore.
        const assignedGoals = (statsByMatch.get(match.id) || []).reduce((acc, s) => acc + (s.goals || 0), 0)
          + (match.opponent_own_goals || 0);
        const teamGoals = match.our_score || 0;
        return { match, assignedGoals, teamGoals, delta: teamGoals - assignedGoals };
      })
      .filter((row) => row.teamGoals > 0 && row.assignedGoals < row.teamGoals)
      .sort((a, b) => b.delta - a.delta);
  }, [matchesQuery.data, statsByMatch]);
  const mismatchIds = useMemo(() => new Set(goalMismatches.map((row) => row.match.id)), [goalMismatches]);

  const getVisibleMatches = (event: Session) => {
    const needle = matchSearchTerm.trim().toLowerCase();
    return (matchesQuery.data || [])
      .filter((m) => m.session_id === event.id)
      .filter((m) => {
        if (showOnlyMismatches && !mismatchIds.has(m.id)) return false;
        if (!needle) return true;
        return `${event.title} ${m.opponent_name} ${m.competition_name || ""}`.toLowerCase().includes(needle);
      })
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  };

  const visibleEvents = (eventsQuery.data || []).filter((event) => {
    if (!matchSearchTerm.trim() && !showOnlyMismatches) return true;
    return getVisibleMatches(event).length > 0;
  });
  const creatorsMap = useUsersDisplay(visibleEvents.map((e) => e.created_by));

  // Statistiche aggregate: solo match con risultato (our_score e opponent_score non null)
  const playedMatches = (matchesQuery.data || []).filter(
    (m) => m.counts_for_stats !== false && m.our_score !== null && m.opponent_score !== null,
  );
  let wins = 0;
  let draws = 0;
  let losses = 0;
  let goalsFor = 0;
  let goalsAgainst = 0;
  for (const m of playedMatches) {
    const our = m.our_score || 0;
    const opp = m.opponent_score || 0;
    goalsFor += our;
    goalsAgainst += opp;
    if (our > opp) wins += 1;
    else if (our < opp) losses += 1;
    else draws += 1;
  }
  const playerGoalAssist = new Map<string, { goals: number; assists: number; cards: number }>();
  const playedMatchIds = new Set(playedMatches.map((m) => m.id));
  for (const s of statsQuery.data || []) {
    if (!playedMatchIds.has(s.match_id)) continue;
    const cur = playerGoalAssist.get(s.player_id) || { goals: 0, assists: 0, cards: 0 };
    cur.goals += s.goals || 0;
    cur.assists += s.assists || 0;
    cur.cards += (s.yellow_cards || 0) + (s.red_cards || 0);
    playerGoalAssist.set(s.player_id, cur);
  }
  const findTopPlayer = (key: "goals" | "assists" | "cards") => {
    let topId: string | null = null;
    let topVal = 0;
    for (const [id, v] of playerGoalAssist.entries()) {
      if (v[key] > topVal) {
        topVal = v[key];
        topId = id;
      }
    }
    if (!topId || topVal === 0) return null;
    const player = (playersQuery.data || []).find((p) => p.id === topId);
    return player ? { name: player.display_name, value: topVal } : null;
  };
  const topScorer = findTopPlayer("goals");
  const topAssist = findTopPlayer("assists");

  return (
    <div className="space-y-3 animate-fade-in">
      <TeamSuggestions
        opponents={opponentsQuery.data || []}
        competitions={competitionsQuery.data || []}
      />

      <section className="flex flex-wrap gap-2 items-center">
        <p className="text-[13px] text-[hsl(var(--faint))]">
          {t("matchdays.summary", { count: visibleEvents.length })}
          {goalMismatches.length > 0
            ? t("matchdays.summaryCorrections", { count: goalMismatches.length })
            : ""}
        </p>
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap gap-2">
          <div className="relative w-full sm:w-44">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={matchSearchTerm}
              onChange={(e) => setMatchSearchTerm(e.target.value)}
              placeholder={t("matchdays.searchOpponent")}
              className="pl-9"
            />
          </div>
          <div className="relative w-full sm:w-44">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={responseSearchTerm}
              onChange={(e) => setResponseSearchTerm(e.target.value)}
              placeholder={t("matchdays.searchCallup")}
              className="pl-9"
            />
          </div>
          <label className="w-full sm:w-auto flex items-center gap-2 px-4 min-h-11 rounded-full bg-raised text-sm">
            <Switch checked={showOnlyMismatches} onCheckedChange={setShowOnlyMismatches} />
            {t("matchdays.onlyMismatches")}
          </label>
        </div>
      </section>

      {goalMismatches.length > 0 && (
        <Card>
          <details open>
            <summary className="cursor-pointer list-none px-4 py-3 min-h-11 flex items-center justify-between gap-3 select-none">
              <div className="flex items-center gap-2 min-w-0">
                <span aria-hidden className="h-3.5 w-2.5 rounded-[3px] bg-[hsl(var(--warn))] shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold leading-tight truncate">
                    {t("matchdays.fixSectionTitle")} <span className="text-muted-foreground font-normal">({goalMismatches.length})</span>
                  </p>
                  <p className="text-[13px] text-[hsl(var(--faint))] leading-tight truncate">
                    {t("matchdays.fixSectionHint")}
                  </p>
                </div>
              </div>
              <span className="text-[13px] text-[hsl(var(--faint))] shrink-0">{t("matchdays.fixOpen")}</span>
            </summary>
            <div className="px-4 pb-2">
              {goalMismatches.slice(0, 6).map(({ match, assignedGoals, teamGoals, delta }) => (
                <button
                  key={match.id}
                  onClick={() => {
                    setOpenEventId(match.session_id);
                    setOpenMatchId(match.id);
                    setScrollToMatchId(match.id);
                  }}
                  className="w-full flex items-center justify-between gap-3 border-t first:border-t-0 py-2.5 min-h-11 text-left"
                >
                  <div>
                    <p className="font-medium">{match.opponent_name}</p>
                    <p className="text-[13px] text-[hsl(var(--faint))]">
                      {t("matchdays.fixAssigned", { assigned: assignedGoals, total: teamGoals })}
                    </p>
                  </div>
                  <Badge variant="secondary" className="shrink-0 text-[hsl(var(--warn))]">
                    {t("matchdays.fixToAssign", { count: delta })}
                  </Badge>
                </button>
              ))}
            </div>
          </details>
        </Card>
      )}

      {eventsQuery.isLoading || matchesQuery.isLoading ? (
        <SectionLoading />
      ) : visibleEvents.length === 0 ? (
        <EmptyState text={t("matchdays.noMatchdays")} />
      ) : (
        <div className="flex flex-col gap-2.5">
          <TimeSections items={visibleEvents} render={(event) => {
            const eventMatches = getVisibleMatches(event);
            const rosterPlayers = (playersQuery.data || []).filter(isSquadPlayer);
            const guestPlayers = (playersQuery.data || []).filter(isEvaluatingGuest);
            const summary = eventMatches.slice(0, 3).map((m) => m.opponent_name).join(", ");
            const isOpen = openEventId === event.id;
            const eventStart = new Date(event.starts_at);
            const nowMs = Date.now();
            const todayKey = new Date().toDateString();
            const isPast = eventStart.getTime() < nowMs;
            const isToday = !isPast && eventStart.toDateString() === todayKey;
            const eventResponses = (responsesQuery.data || []).filter((r) => r.session_id === event.id);
            const counts = getResponseCounts(eventResponses, rosterPlayers.length);
            const eventCallups = (callupsQuery.data || []).filter(
              (c) => c.session_id === event.id && c.status === "confirmed",
            );
            const callupCount = eventCallups.length;
            const pendingNotifyCount = eventCallups.filter((c) => !c.notified_at).length;
            const totalSlots = Math.max(rosterPlayers.length, 1);
            const presentPct = Math.min(100, (counts.present / totalSlots) * 100);
            const latePct = Math.min(100 - presentPct, (counts.late / totalSlots) * 100);
            const absentPct = Math.min(100 - presentPct - latePct, (counts.absent / totalSlots) * 100);
            const deadlineDate = event.response_deadline_at ? new Date(event.response_deadline_at) : null;
            const deadlinePassed = !!deadlineDate && deadlineDate.getTime() < nowMs;
            const creator = event.created_by ? creatorsMap.get(event.created_by) : null;
            // Risultati aggregati delle partite della giornata
            let dayW = 0, dayD = 0, dayL = 0, dayGF = 0, dayGA = 0, dayPlayed = 0;
            for (const m of eventMatches) {
              if (m.counts_for_stats === false || m.our_score == null || m.opponent_score == null) continue;
              dayPlayed += 1;
              dayGF += m.our_score;
              dayGA += m.opponent_score;
              if (m.our_score > m.opponent_score) dayW += 1;
              else if (m.our_score < m.opponent_score) dayL += 1;
              else dayD += 1;
            }
            const onCopyMessageClick = () =>
              copySessionMessage.mutate(event, {
                onSuccess: ({ text, title }) => {
                  setCopiedMessageEventId(event.id);
                  setShareDialog({ open: true, title, text });
                },
              });
            return (
              <Card key={event.id} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenEventId(isOpen ? null : event.id)}
                  className="w-full text-left hover:bg-raised/60 transition-colors"
                >
                  <div className="flex items-center gap-3 sm:gap-4 p-4">
                    <SessionDate date={eventStart} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-[15px] font-semibold leading-tight truncate min-w-0">{event.title}</h3>
                        {isToday && <Badge className="h-5 px-2 text-xs">{t("events.card.today")}</Badge>}
                        {event.include_guests && (
                          <Badge variant="secondary" className="h-5 px-2 text-xs gap-1">
                            <UserPlus className="h-4 w-4" />
                            {t("events.card.guests")}
                          </Badge>
                        )}
                      </div>
                      {summary && <p className="mt-0.5 truncate"><span className="text-[hsl(var(--faint))]">vs</span> {summary}</p>}
                      <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-[13px] text-[hsl(var(--faint))]">
                        <span>
                          {t("matchdays.matchesCount", {
                            count: eventMatches.length,
                            label: eventMatches.length === 1 ? t("matchdays.matchSingular") : t("matchdays.matchPlural"),
                          })}
                        </span>
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <Clock className="h-3.5 w-3.5" />
                          {eventStart.toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {event.location && (
                          <span className="inline-flex items-center gap-1 truncate max-w-[16ch] sm:max-w-[28ch]">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </span>
                        )}
                        {creator && <span className="truncate">{t("events.card.createdBy", { name: userShortName(creator) })}</span>}
                      </div>
                    </div>
                    {dayPlayed > 0 ? (
                      <div className="shrink-0 text-right">
                        <p className={cn("eb-num text-[28px] pr-1", dayGF > dayGA && "text-[hsl(var(--ok))]", dayGF < dayGA && "text-[hsl(var(--danger))]")}>{dayGF}-{dayGA}</p>
                        <p className="flex justify-end gap-1.5 text-[13px] text-[hsl(var(--faint))] tabular-nums">
                          <span><span className="font-medium text-foreground">{dayW}</span> V</span>
                          <span><span className="font-medium text-foreground">{dayD}</span> N</span>
                          <span><span className="font-medium text-foreground">{dayL}</span> P</span>
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="hidden md:flex items-center gap-3 shrink-0">
                          <CountMark tone="present" label={t("events.card.presentShort")} value={counts.present} />
                          <CountMark tone="late" label={t("events.card.lateShort")} value={counts.late} />
                          <CountMark tone="absent" label={t("events.card.absentShort")} value={counts.absent} />
                          <div className="text-center min-w-[52px] pl-3 border-l">
                            <p className="font-medium tabular-nums">{callupCount}/{rosterPlayers.length}</p>
                            <p className="text-[13px] text-[hsl(var(--faint))]">{t("events.card.callupsShort")}</p>
                          </div>
                        </div>
                        <div className="md:hidden text-right shrink-0 min-w-[48px]">
                          <p className="font-medium tabular-nums">{counts.present + counts.late}/{rosterPlayers.length}</p>
                          <p className="text-[13px] text-[hsl(var(--faint))]">{t("events.card.presentShort")}</p>
                        </div>
                      </>
                    )}
                    <span className="shrink-0 text-[hsl(var(--faint))]">
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </span>
                  </div>
                  <AttendanceBar present={presentPct} late={latePct} absent={absentPct} />
                </button>
                <div className="flex items-center gap-x-3 gap-y-1 px-4 py-2 flex-wrap text-[13px] text-muted-foreground">
                  {deadlineDate && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className={cn("h-3.5 w-3.5", !deadlinePassed && "text-[hsl(var(--warn))]")} />
                      {deadlinePassed
                        ? t("events.card.deadlineClosed")
                        : t("events.card.deadlineOpen", { when: formatDateTime(event.response_deadline_at) })}
                    </span>
                  )}
                  {callupCount > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      {t("events.card.callups")}: <strong className="font-medium tabular-nums text-foreground">{callupCount}</strong>
                    </span>
                  )}
                  <div className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-11 sm:h-9 px-3 text-sm"
                      disabled={createMagicLink.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        createMagicLink.mutate({ event, mode: "open" });
                      }}
                    >
                      <ExternalLink className="h-4 w-4" />
                      {t("events.card.openLink")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-11 sm:h-9 px-3 text-sm"
                      disabled={copySessionMessage.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyMessageClick();
                      }}
                    >
                      <MessageSquare className="h-4 w-4" />
                      {t("events.card.message")}
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t">
                    <SessionToolbar
                      event={event}
                      isSavingEdit={updateMatchDayEvent.isPending}
                      onSaveEdit={(payload) => updateMatchDayEvent.mutate(payload)}
                      isDuplicating={duplicateMatchDay.isPending}
                      onDuplicate={() => duplicateMatchDay.mutate(event)}
                      isCreatingLink={createMagicLink.isPending}
                      onOpenLink={() => createMagicLink.mutate({ event, mode: "open" })}
                      onCopyLink={() => createMagicLink.mutate({ event, mode: "copy" })}
                      isCopyingMessage={copySessionMessage.isPending}
                      onCopyMessage={() =>
                        copySessionMessage.mutate(event, {
                          onSuccess: ({ text, title }) => {
                            setCopiedMessageEventId(event.id);
                            setShareDialog({ open: true, title, text });
                          },
                        })
                      }
                      copiedMessage={copiedMessageEventId === event.id}
                      isCopyingCallups={copyCallupList.isPending}
                      onCopyCallups={() => {
                        const names = (callupsQuery.data || [])
                          .filter((c) => c.session_id === event.id && c.status === "confirmed")
                          .map((c) => rosterPlayers.find((p) => p.id === c.player_id)?.display_name)
                          .filter((n): n is string => !!n)
                          .sort((a, b) => a.localeCompare(b));
                        copyCallupList.mutate(
                          { event, names },
                          {
                            onSuccess: ({ text, title }) => {
                              setCopiedCallupsEventId(event.id);
                              setShareDialog({ open: true, title, text });
                            },
                          },
                        );
                      }}
                      copiedCallups={copiedCallupsEventId === event.id}
                      callupCount={callupCount}
                      pendingNotifyCount={pendingNotifyCount}
                      isNotifying={notifyCallups.isPending}
                      onNotifyCallups={() => onNotifyCallupsForEvent(event.id, pendingNotifyCount)}
                      isDeleting={deleteMatchDay.isPending}
                      onDelete={() => {
                        if (window.confirm(t("matchdays.confirmDelete", { title: event.title })))
                          deleteMatchDay.mutate(event.id);
                      }}
                    />
                    <div className="grid gap-4 px-2 pb-3 sm:px-4 xl:grid-cols-[minmax(0,400px)_minmax(0,1fr)]">
                      <details className="xl:hidden border-y">
                        <summary className="cursor-pointer min-h-11 px-2 py-2 font-medium select-none flex items-center justify-between gap-3">
                          <span>{t("matchdays.expandAttendance")}</span>
                          <span className="text-[13px] text-[hsl(var(--faint))]">{t("matchdays.expandHint")}</span>
                        </summary>
                        <div className="pb-3">
                          <SessionAttendanceBoard
                            event={event}
                            players={rosterPlayers}
                            guestPlayers={guestPlayers}
                            responses={(responsesQuery.data || []).filter((r) => r.session_id === event.id)}
                            callups={(callupsQuery.data || []).filter((c) => c.session_id === event.id)}
                            searchTerm={responseSearchTerm}
                            isSaving={updateSessionResponse.isPending}
                            isCallupSaving={updateCallup.isPending || callupPresent.isPending}
                            onUpdate={(playerId, status) => updateSessionResponse.mutate({ eventId: event.id, playerId, status })}
                            onToggleCallup={(playerId, shouldCallup) => updateCallup.mutate({ eventId: event.id, playerId, shouldCallup })}
                            onCallupPresent={(playerIds) => callupPresent.mutate({ eventId: event.id, playerIds })}
                            onRefreshResponses={() =>
                              Promise.all([
                                queryClient.invalidateQueries({ queryKey: ["responses", teamId, "matchdays"] }),
                                queryClient.invalidateQueries({ queryKey: ["event-callups", teamId] }),
                              ])
                            }
                          />
                        </div>
                      </details>
                      <div className="hidden xl:block xl:sticky xl:top-20 xl:self-start xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto">
                        <SessionAttendanceBoard
                          singleColumn
                          event={event}
                          players={rosterPlayers}
                          guestPlayers={guestPlayers}
                          responses={(responsesQuery.data || []).filter((r) => r.session_id === event.id)}
                          callups={(callupsQuery.data || []).filter((c) => c.session_id === event.id)}
                          searchTerm={responseSearchTerm}
                          isSaving={updateSessionResponse.isPending}
                          isCallupSaving={updateCallup.isPending || callupPresent.isPending}
                          onUpdate={(playerId, status) => updateSessionResponse.mutate({ eventId: event.id, playerId, status })}
                          onToggleCallup={(playerId, shouldCallup) => updateCallup.mutate({ eventId: event.id, playerId, shouldCallup })}
                          onCallupPresent={(playerIds) => callupPresent.mutate({ eventId: event.id, playerIds })}
                          onRefreshResponses={() =>
                            Promise.all([
                              queryClient.invalidateQueries({ queryKey: ["responses", teamId, "matchdays"] }),
                              queryClient.invalidateQueries({ queryKey: ["event-callups", teamId] }),
                            ])
                          }
                        />
                      </div>
                      <div className="space-y-3 min-w-0">
                        <div className="px-2">
                          <h4 className="font-semibold mb-2">{t("matchdays.addMatchTitle")}</h4>
                          <form
                            className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2"
                            onSubmit={(submitEvent) => {
                              submitEvent.preventDefault();
                              track.ctaClick("new_match", { team_id: teamId, session_id: event.id });
                              createMatch.mutate({ event, formData: new FormData(submitEvent.currentTarget) });
                              submitEvent.currentTarget.reset();
                            }}
                          >
                            <Input name="opponent_name" placeholder={t("matchdays.opponentPh")} required list="team-opponents" className="col-span-2 sm:flex-1 sm:min-w-[160px]" />
                            <QuickCompetitionField teamId={teamId} />
                            <Input name="date" type="date" defaultValue={event.starts_at.slice(0, 10)} className="sm:w-[150px]" />
                            <Input name="time" type="time" className="sm:w-[110px]" />
                            <Select name="home_away" defaultValue="neutral">
                              <SelectTrigger className="col-span-2 sm:w-[120px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="home">{t("wizard.matches.home")}</SelectItem>
                                <SelectItem value="away">{t("wizard.matches.away")}</SelectItem>
                                <SelectItem value="neutral">{t("wizard.matches.neutral")}</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input name="our_score" type="number" min="0" placeholder={t("matchdays.homeShort")} className="sm:w-16 text-center" />
                            <Input name="opponent_score" type="number" min="0" placeholder={t("matchdays.awayShort")} className="sm:w-16 text-center" />
                            <Button type="submit" size="sm" className="col-span-2 h-11" disabled={createMatch.isPending}>
                              <Plus className="h-4 w-4" /> {t("matchdays.addBtn")}
                            </Button>
                          </form>
                        </div>

                        <div className="space-y-2">
                      {eventMatches.map((match) => {
                        const stats = (statsQuery.data || []).filter((s) => s.match_id === match.id);
                        const statRows = stats
                          .map((stat) => ({
                            ...stat,
                            player: (playersQuery.data || []).find((p) => p.id === stat.player_id),
                          }))
                          .sort((a, b) => {
                            if (!!b.started !== !!a.started) return b.started ? 1 : -1;
                            const scoreDelta = (b.goals + b.assists) - (a.goals + a.assists);
                            if (scoreDelta !== 0) return scoreDelta;
                            return (a.player?.display_name || "").localeCompare(b.player?.display_name || "");
                          });
                        void statRows;
                        const assignedGoals = stats.reduce((acc, s) => acc + (s.goals || 0), 0);
                        const matchAssists = stats.reduce((acc, s) => acc + (s.assists || 0), 0);
                        const startersCount = stats.filter((s) => s.started).length;
                        const matchDayPresentIds = (responsesQuery.data || [])
                          .filter((r) => r.session_id === match.session_id)
                          .filter((r) => r.status === "present" || r.status === "late")
                          .map((r) => r.player_id);
                        const isMatchOpen = openMatchId === match.id;
                        const score =
                          match.our_score !== null && match.opponent_score !== null
                            ? `${match.our_score}-${match.opponent_score}`
                            : t("matchdays.scoreNa");
                        const matchOutcome = (() => {
                          if (match.our_score == null || match.opponent_score == null) return null;
                          if (match.our_score > match.opponent_score) return "win";
                          if (match.our_score < match.opponent_score) return "loss";
                          return "draw";
                        })();
                        return (
                          <Card key={match.id} id={`match-${match.id}`} className="rounded-none bg-transparent border-t scroll-mt-20">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-2">
                              <button
                                type="button"
                                onClick={() => setOpenMatchId(isMatchOpen ? null : match.id)}
                                className="flex-1 basis-full sm:basis-0 flex items-center gap-3 min-h-11 min-w-0 text-left"
                              >
                                <span
                                  className={cn(
                                    "score-chip shrink-0",
                                    matchOutcome === "win" && "win",
                                    matchOutcome === "draw" && "draw",
                                    matchOutcome === "loss" && "lose",
                                    matchOutcome === null && "bg-raised text-muted-foreground"
                                  )}
                                >
                                  {score}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold truncate leading-tight">
                                    {t("matchdays.vs")} {match.opponent_name}
                                    {match.counts_for_stats === false && (
                                      <span
                                        className="ml-1.5 align-middle px-2 py-0.5 rounded-full bg-raised text-muted-foreground text-xs font-medium"
                                        title={t("matchdays.excludedTitle")}
                                      >
                                        {t("matchdays.excludedBadge")}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-[13px] text-[hsl(var(--faint))] truncate leading-tight">
                                    {t(`wizard.matches.${match.home_away}`)}
                                    {match.competition_name ? ` · ${match.competition_name}` : ""}
                                    {match.starts_at ? ` · ${new Date(match.starts_at).toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" })}` : ""}
                                  </p>
                                </div>
                              </button>
                              <div className="ml-auto flex items-center gap-2 text-[13px] text-muted-foreground">
                                {(match.our_score ?? 0) > 0 && assignedGoals + (match.opponent_own_goals || 0) < (match.our_score ?? 0) && (
                                  <span className="font-medium text-[hsl(var(--warn))]" title={t("matchdays.toAssignTitle")}>
                                    {t("matchdays.toAssignHint", { count: (match.our_score ?? 0) - assignedGoals - (match.opponent_own_goals || 0) })}
                                  </span>
                                )}
                                <span className="tabular-nums"><strong className="text-foreground">{matchAssists}</strong>A</span>
                                <span className="tabular-nums"><strong className="text-foreground">{startersCount}</strong>T</span>
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 p-0" disabled={duplicateMatch.isPending} onClick={() => duplicateMatch.mutate(match)} title={t("matchdays.duplicate")}>
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-11 w-11 sm:h-9 sm:w-9 p-0 text-[hsl(var(--danger))]"
                                  disabled={deleteMatch.isPending}
                                  onClick={() => {
                                    if (window.confirm(t("matchdays.deleteMatchConfirm", { opponent: match.opponent_name })))
                                      deleteMatch.mutate(match.id);
                                  }}
                                  title={t("matchdays.delete")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            {isMatchOpen && (
                              <MatchOpenPanel
                                match={match}
                                stats={stats}
                                assignedGoals={assignedGoals}
                                startersCount={startersCount}
                                matchDayPresentIds={matchDayPresentIds}
                                rosterPlayers={(playersQuery.data || []).filter(isSquadPlayer)}
                                presentPlayers={(playersQuery.data || []).filter((p) => matchDayPresentIds.includes(p.id))}
                                isMarkingStarters={markStarters.isPending}
                                isClearingStarters={clearStarters.isPending}
                                isUpdatingScore={updateMatchScore.isPending}
                                isUpdatingMatch={updateMatch.isPending}
                                onUpsertStat={(payload) => upsertPlayerStat.mutate(payload)}
                                onMarkStarters={() => markStarters.mutate({ matchId: match.id, playerIds: matchDayPresentIds })}
                                onClearStarters={() => {
                                  if (window.confirm(t("matchdays.resetStartersConfirm", { opponent: match.opponent_name })))
                                    clearStarters.mutate({ matchId: match.id });
                                }}
                                onUpdateScore={(ourScore, opponentScore) =>
                                  updateMatchScore.mutate({ matchId: match.id, ourScore, opponentScore })
                                }
                                onUpdateMatch={(payload) => updateMatch.mutate({ matchId: match.id, payload })}
                              />
                            )}
                          </Card>
                        );
                      })}
                      {eventMatches.length === 0 && <EmptyState text={t("matchdays.noMatchesAttached")} />}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          }} />
        </div>
      )}
      {playedMatches.length > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <MatchStatTile label={t("matchdays.stats.played")} value={playedMatches.length} accent="text-foreground" />
          <MatchStatTile label={t("matchdays.stats.wins")} value={wins} accent="text-emerald-600" />
          <MatchStatTile label={t("matchdays.stats.draws")} value={draws} accent="text-foreground" />
          <MatchStatTile label={t("matchdays.stats.losses")} value={losses} accent="text-foreground" />
          <MatchStatTile label={t("matchdays.stats.goalsFor")} value={goalsFor} accent="text-foreground" />
          <MatchStatTile label={t("matchdays.stats.goalsAgainst")} value={goalsAgainst} accent="text-foreground" />
          <MatchStatTile
            label={t("matchdays.stats.topScorer")}
            value={topScorer?.name || "—"}
            hint={topScorer ? t("matchdays.stats.goalsHint", { count: topScorer.value }) : ""}
            accent="text-foreground"
            small
          />
          <MatchStatTile
            label={t("matchdays.stats.topAssist")}
            value={topAssist?.name || "—"}
            hint={topAssist ? t("matchdays.stats.assistsHint", { count: topAssist.value }) : ""}
            accent="text-foreground"
            small
          />
        </section>
      )}
      <ShareTextDialog
        open={shareDialog.open}
        onOpenChange={(open) => setShareDialog((s) => ({ ...s, open }))}
        title={shareDialog.title}
        text={shareDialog.text}
      />
    </div>
  );
}

function MatchStatTile({
  label,
  value,
  hint,
  accent,
  small = false,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: string;
  small?: boolean;
}) {
  return (
    <div className="rounded-2xl bg-card px-4 py-3">
      <StatTileLabel>{label}</StatTileLabel>
      <p
        className={cn(
          "truncate",
          small ? "font-semibold mt-1" : "eb-num text-[34px] mt-1.5 pr-1",
          accent,
        )}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </p>
      {hint && <p className="text-[13px] text-[hsl(var(--faint))] mt-0.5 truncate">{hint}</p>}
    </div>
  );
}
